// lib/store/slices/trackingSlice.ts
import { createSlice, createAsyncThunk, type PayloadAction } from '@reduxjs/toolkit';
import { apiMethods } from '../../services/api';
import type { TrackingState, TrackPageVisitPayload, TrackPageVisitResponse, ApiResponse } from '@/types';

const initialState: TrackingState = {
    visitorId: null,
    loading: false,
    error: null,
};

// ── POST /tracking/track ──────────────────────────────────────────────────────

export const trackPageVisit = createAsyncThunk(
    'tracking/trackPageVisit',
    async (payload: TrackPageVisitPayload, { rejectWithValue }) => {
        try {
            const response = await apiMethods.post<ApiResponse<TrackPageVisitResponse>>(
                '/tracking/track',
                payload
            );
            return response.data;
        } catch (error: any) {
            return rejectWithValue(error.message || 'Failed to track page visit');
        }
    }
);

// ── PATCH /tracking/pageview/:id ──────────────────────────────────────────────

export const updatePageView = createAsyncThunk(
    'tracking/updatePageView',
    async (
        { id, data }: { id: string; data: { timeOnPage?: number; scrollDepth?: number; isExit?: boolean } },
        { rejectWithValue }
    ) => {
        try {
            await apiMethods.patch(`/tracking/pageview/${id}`, data);
        } catch (error: any) {
            return rejectWithValue(error.message || 'Failed to update page view');
        }
    }
);

// ── Slice ─────────────────────────────────────────────────────────────────────

const trackingSlice = createSlice({
    name: 'tracking',
    initialState,
    reducers: {
        clearError: (state) => { state.error = null; },
        setVisitorId: (state, action: PayloadAction<string>) => {
            state.visitorId = action.payload;
        },
    },

    extraReducers: (builder) => {
        builder
            .addCase(trackPageVisit.pending, (state) => { state.loading = true; state.error = null; })
            .addCase(trackPageVisit.fulfilled, (state, { payload }) => {
                state.loading   = false;
                state.visitorId = payload.visitorId;
            })
            .addCase(trackPageVisit.rejected, (state, { payload }) => {
                state.loading = false;
                state.error   = payload as string;
            });

        builder
            .addCase(updatePageView.pending, (state) => { state.loading = true; })
            .addCase(updatePageView.fulfilled, (state) => { state.loading = false; })
            .addCase(updatePageView.rejected, (state, { payload }) => {
                state.loading = false;
                state.error   = payload as string;
            });
    },
});

export const { clearError, setVisitorId } = trackingSlice.actions;
export default trackingSlice.reducer;
